import { useReducedMotion } from 'framer-motion'
import { TrendingUp } from 'lucide-react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  buildPipelineClosingsChartData,
  hasPipelineClosingsData,
  type PipelineTabView,
} from '@/components/Dashboard/pipelineTab.utils'
import {
  formatDashboardCurrency,
  formatDashboardNumber,
} from '@/utils/crmValuesDisplay'

type Props = {
  view: PipelineTabView
  valuesVisible?: boolean
}

export function PipelineClosingsChart({ view, valuesVisible = false }: Props) {
  const reduced = useReducedMotion()
  const chartData = buildPipelineClosingsChartData(view.monthlyData)
  const hasData = hasPipelineClosingsData(chartData)

  return (
    <div className='rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-sm'>
      <div className='mb-5 flex flex-wrap items-start justify-between gap-3'>
        <div>
          <div className='flex items-center gap-2'>
            <TrendingUp size={16} className='text-accent' aria-hidden />
            <h3 className='text-base font-semibold text-text-primary'>Fechamentos mensais</h3>
          </div>
          <p className='mt-1 text-sm text-text-muted'>
            Receita fechada (barras) e volume de deals ganhos (linha)
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-3 text-xs text-text-muted'>
          <span className='inline-flex items-center gap-1.5'>
            <span className='h-2.5 w-2.5 rounded-sm bg-[var(--brand-primary)]' aria-hidden />
            Receita
          </span>
          <span className='inline-flex items-center gap-1.5'>
            <span className='h-0.5 w-4 rounded bg-emerald-500' aria-hidden />
            Deals
          </span>
        </div>
      </div>

      {hasData ? (
        <>
          <ResponsiveContainer width='100%' height={280}>
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray='3 3' stroke='var(--border)' vertical={false} />
              <XAxis
                dataKey='month'
                tick={{ fill: 'var(--font-color-muted)', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId='left'
                tick={{ fill: 'var(--font-color-muted)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={48}
              />
              <YAxis
                yAxisId='right'
                orientation='right'
                allowDecimals={false}
                tick={{ fill: 'var(--font-color-muted)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={32}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                }}
                formatter={(rawValue, rawName) => {
                  const value = Number(rawValue ?? 0)
                  const name = String(rawName ?? '')
                  if (name === 'receita') {
                    return [formatDashboardCurrency(value, valuesVisible), 'Receita fechada']
                  }
                  return [formatDashboardNumber(value, valuesVisible), 'Deals ganhos']
                }}
              />
              <Bar
                yAxisId='left'
                dataKey='receita'
                fill='var(--brand-primary)'
                radius={[6, 6, 0, 0]}
                maxBarSize={48}
                isAnimationActive={!reduced}
              />
              <Line
                yAxisId='right'
                type='monotone'
                dataKey='deals'
                stroke='#10b981'
                strokeWidth={2.5}
                dot={{ r: 3, fill: '#10b981', strokeWidth: 0 }}
                isAnimationActive={!reduced}
              />
            </ComposedChart>
          </ResponsiveContainer>

          <div className='mt-4 space-y-1 border-t border-[var(--border)] pt-4'>
            {chartData.map((row) => (
              <div key={row.month} className='flex items-center justify-between py-2 text-sm'>
                <span className='text-text-secondary'>{row.month}</span>
                <span className='font-bold tabular-nums text-text-primary'>
                  {formatDashboardCurrency(row.receita, valuesVisible)}
                  <span className='ml-2 text-xs font-medium text-text-muted'>
                    · {formatDashboardNumber(row.deals, valuesVisible)} deals
                  </span>
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className='flex h-[280px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--border)] bg-[var(--bg-dark)]/10 px-6 text-center'>
          <p className='text-sm font-medium text-text-primary'>Nenhum fechamento no período</p>
          <p className='max-w-md text-sm text-text-muted'>
            {view.pipelineCount > 0
              ? 'Há negociações abertas, mas nenhum ganho com data de fechamento neste intervalo.'
              : 'Ajuste o período no topo da página para ver o histórico de fechamentos.'}
          </p>
        </div>
      )}
    </div>
  )
}
